<template>
  <main class="editorial-home">
    <section v-if="loading" class="home-skeleton"><div></div><aside><span v-for="i in 3" :key="i"></span></aside></section>

    <section v-else class="conversion-hero">
      <div class="conversion-copy">
        <span class="beta-pill">抢先体验版</span>
        <h1>上传一张图，<br />看看它变成拼豆挂件</h1>
        <p>选择用途，系统自动处理构图、颜色和拼豆尺寸。</p>

        <div class="scene-picker" role="radiogroup" aria-label="选择生成用途">
          <button v-for="scene in creationScenes" :key="scene.key" type="button" role="radio"
                  :aria-checked="selectedScene===scene.key" :class="{active:selectedScene===scene.key}"
                  @click="selectedScene=scene.key">
            <component :is="scene.icon" :size="20" />
            <span><strong>{{ scene.label }}</strong><small>{{ scene.hint }}</small></span>
          </button>
        </div>

        <input ref="homeFileInput" class="home-file-input" type="file" accept="image/jpeg,image/png,image/webp"
               @change="handleHomeFileChange" />
        <div class="conversion-actions">
          <button class="upload-primary" type="button" :disabled="homeUploadBusy" @click="homeFileInput?.click()">
            <UploadCloud :size="21" /> {{ homeUploadBusy ? "正在导入图片" : "上传图片，免费生成" }}
          </button>
        </div>
        <small class="upload-note">支持 JPG、PNG、WebP · 无需登录即可预览</small>
        <div class="compact-flow" aria-label="生成流程">
          <span><i>1</i>上传</span>
          <span><i>2</i>生成</span>
          <span><i>3</i>预览</span>
          <span><i>4</i>下载分享</span>
        </div>
      </div>

      <div class="result-showcase" aria-label="生成效果示例"
           @mouseenter="previewPaused=true" @mouseleave="previewPaused=false"
           @focusin="previewPaused=true" @focusout="previewPaused=false">
        <header><span>生成效果</span><strong>{{ activePreviewStage.label }}</strong></header>
        <div class="showcase-image" :class="{webgl:activePreviewStage.key==='threeD'}" @dblclick="openShowcaseWork">
          <BeadWebGLViewer
            v-if="activePreviewStage.key==='threeD'"
            :pixels="showcaseWork?.pixels||[]"
            :grid-width="showcaseWork?.gridWidth||50"
            :grid-height="showcaseWork?.gridHeight||0"
            :image-source="showcaseWork?.detailImage||showcaseImage"
            :auto-rotate="!previewPaused"
            :full-board="true"
          />
          <Transition v-else name="showcase-slide" mode="out-in">
            <img :key="`${activePreviewStage.key}-${showcaseImage}`" :src="showcaseImage"
                 :alt="`${activePreviewStage.label}示例`" decoding="async" fetchpriority="high"
                 @click="openShowcaseWork" />
          </Transition>
        </div>
        <div class="stage-tabs" role="tablist" aria-label="查看生成步骤">
          <button v-for="stage in previewStages" :key="stage.key" type="button" role="tab"
                  :aria-selected="previewStage===stage.key" :class="{active:previewStage===stage.key}"
                  @click="selectPreviewStage(stage.key)">
            <component :is="stage.icon" :size="16" />{{ stage.label }}
          </button>
        </div>
      </div>
    </section>

    <section class="recommendations">
      <header><h2>为你推荐</h2>
        <button type="button" @click="shuffleRecommendations">换一批
          <RefreshCw :size="16"/>
        </button>
      </header>
      <div v-if="works.length" class="editorial-grid">
        <WorkCard v-for="work in displayedWorks" :key="work.id" :work="work" @favorite="favoriteWork"/>
      </div>
      <div v-else class="empty-recommend"><p>社区灵感暂时没有加载出来</p>
        <button type="button" @click="loadWorks">重新加载</button>
      </div>
    </section>

  </main>
</template>

<script setup>
import {computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref, watch} from "vue";
import {useRouter} from "vue-router";
import {
  Box,
  Gift,
  Grid2X2,
  Image as ImageIcon,
  PawPrint,
  RefreshCw,
  Sparkles,
  UploadCloud,
  UserRound
} from "@lucide/vue";
import WorkCard from "../components/WorkCard.vue";
import {createWorksApi} from "../api/worksApi";
import {normalizeWork, normalizeWorkPage} from "../utils/work";
import {useStudio} from "../composables/useStudio";
import {useAnalytics} from "../composables/useAnalytics";
import {useToast} from "../composables/useToast";
import {buildCosThumbnail} from "../utils/imageUrl";
import {createPatternPreviewDataUrl} from "../utils/beadArtwork";

const BeadWebGLViewer = defineAsyncComponent(() => import("../components/BeadWebGLViewer.vue"));

const api = createWorksApi();
const router = useRouter();
const {importImageDataUrl} = useStudio();
const {track} = useAnalytics();
const {toast} = useToast();
const works = ref([]);
const loading = ref(true);
const recommendationOffset = ref(0);
const selectedScene = ref("pet");
const previewStage = ref("twoD");
const previewPaused = ref(false);
const homeFileInput = ref(null);
const homeUploadBusy = ref(false);
const showcaseImage = ref("");
const showcaseWork = ref(null);
let showcaseLoadToken = 0;
let previewTimer = 0;
const creationScenes = [
  {key:"pet",label:"宠物",hint:"保留轮廓与表情",icon:PawPrint},
  {key:"avatar",label:"头像",hint:"优先处理五官",icon:UserRound},
  {key:"charm",label:"小挂件",hint:"简化成清晰造型",icon:Gift}
];
const previewStages = [
  {key:"original",label:"原图",icon:ImageIcon,fallback:`${process.env.BASE_URL}assets/membership/journey-2.webp`},
  {key:"cartoon",label:"卡通",icon:Sparkles,fallback:`${process.env.BASE_URL}assets/membership/journey-1.webp`},
  {key:"twoD",label:"2D 拼豆",icon:Grid2X2,fallback:`${process.env.BASE_URL}assets/membership/journey-3.webp`},
  {key:"threeD",label:"3D 挂件",icon:Box,fallback:`${process.env.BASE_URL}assets/membership/journey-4.webp`}
];
const activePreviewStage = computed(() => previewStages.find(item => item.key === previewStage.value) || previewStages[1]);
const showcaseCandidate = computed(() => {
  const work = showcaseWork.value;
  if (!work) return activePreviewStage.value.fallback;
  if (previewStage.value === "twoD" || previewStage.value === "threeD") {
    if (!work.pixels?.length) return activePreviewStage.value.fallback;
    if (previewStage.value === "twoD") {
      return createPatternPreviewDataUrl({
        pixels: work.pixels,
        gridWidth: work.gridWidth,
        gridHeight: work.gridHeight,
        marginCells: 2,
        watermark: true
      });
    }
    return work.detailImage || activePreviewStage.value.fallback;
  }
  const source = previewStage.value === "original"
    ? (work.originalImage || work.originalImageUrl)
    : (work.detailImage || work.displayImage || work.thumbnailImage || work.previewImage);
  return source ? buildCosThumbnail(source, 960) : activePreviewStage.value.fallback;
});
const displayedWorks = computed(() => {
  if (works.value.length <= 10) return works.value;
  return Array.from({length: 10}, (_, index) => works.value[(recommendationOffset.value + index) % works.value.length])
});
onMounted(() => {
  loadWorks();
  startPreviewRotation();
});
onBeforeUnmount(stopPreviewRotation);
watch([previewStage,showcaseCandidate],([,candidate])=>{
  const fallback=activePreviewStage.value.fallback;
  showcaseImage.value=fallback;
  if(!candidate||candidate===fallback)return;
  const image=new window.Image();
  image.onload=()=>{if(showcaseCandidate.value===candidate)showcaseImage.value=candidate};
  image.onerror=()=>{if(showcaseCandidate.value===candidate)showcaseImage.value=fallback};
  image.src=candidate;
},{immediate:true});

async function loadWorks() {
  loading.value = true;
  try {
    const home = await api.fetchHome({pageSize: 24});
    works.value = prioritizeThumbnails(normalizeWorkPage(home?.featured).list).slice(0, 12);
  } catch (error) {
    try {
      works.value = prioritizeThumbnails(normalizeWorkPage(await api.fetchWorks({pageNo: 1, pageSize: 24})).list).slice(0, 12)
    } catch (ignore) {
      works.value = []
    }
  } finally {
    chooseShowcaseWork();
    loading.value = false
  }
}

async function chooseShowcaseWork() {
  if (!works.value.length) {
    showcaseWork.value = null;
    return;
  }
  const candidates = works.value.filter(item => item.detailImage || item.displayImage || item.previewImage);
  const ordered = candidates.length ? candidates : works.value;
  const likelyPatternWorks = ordered.filter(item => item.gridWidth && item.gridHeight).sort(() => Math.random() - .5);
  const otherWorks = ordered.filter(item => !item.gridWidth || !item.gridHeight).sort(() => Math.random() - .5);
  const pool = [...likelyPatternWorks, ...otherWorks];
  showcaseWork.value = pool[0] || null;
  const token = ++showcaseLoadToken;
  for (const selected of pool) {
    try {
      const detail = normalizeWork(await api.fetchWork({commentId: selected.id}));
      if (token !== showcaseLoadToken) return;
      if (detail.pixels?.length && detail.gridWidth && detail.gridHeight) {
        showcaseWork.value = detail;
        return;
      }
    } catch (ignore) {
      // Continue looking for a work that can render real 2D/3D bead previews.
    }
  }
  if (token === showcaseLoadToken) showcaseWork.value = pool[0] || null;
}

function prioritizeThumbnails(list = []) {
  // 历史作品尚未补齐缩略图时，不让数 MB 的原图占据首屏下载队列。
  return [...list].sort((a, b) => Number(Boolean(b.thumbnailImage)) - Number(Boolean(a.thumbnailImage)));
}

function openWork(work) {
  if (work?.id) router.push(`/works/${work.id}`)
}

function openShowcaseWork() {
  if (showcaseWork.value?.id) openWork(showcaseWork.value)
}

function selectPreviewStage(key) {
  previewStage.value = key;
  startPreviewRotation();
}

function startPreviewRotation() {
  stopPreviewRotation();
  previewTimer = window.setInterval(() => {
    if (previewPaused.value) return;
    const index = previewStages.findIndex(item => item.key === previewStage.value);
    previewStage.value = previewStages[(index + 1) % previewStages.length].key;
  }, 5600);
}

function stopPreviewRotation() {
  if (previewTimer) window.clearInterval(previewTimer);
  previewTimer = 0;
}

async function handleHomeFileChange(event) {
  const file = event?.target?.files?.[0];
  if (!file) return;
  if (!/^image\/(jpeg|png|webp)$/i.test(file.type || "")) {
    toast.info("请选择 JPG、PNG 或 WebP 图片。", {title:"图片格式不支持"});
    event.target.value = "";
    return;
  }
  homeUploadBusy.value = true;
  try {
    const dataUrl = await readFileAsDataUrl(file);
    const scene = creationScenes.find(item => item.key === selectedScene.value) || creationScenes[0];
    await importImageDataUrl(dataUrl, scene.label);
    track("STUDIO_START", {metadata:{entry:"home",scene:selectedScene.value,type:file.type,size:file.size}});
    track("UPLOAD", {metadata:{entry:"home",scene:selectedScene.value,type:file.type,size:file.size}});
    await router.push({path:"/studio",query:{scene:selectedScene.value,quick:"1",autorun:"1"}});
  } catch (error) {
    toast.error("图片读取失败，请重新选择。", {title:"无法导入图片"});
  } finally {
    homeUploadBusy.value = false;
    if (event?.target) event.target.value = "";
  }
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("READ_FAILED"));
    reader.readAsDataURL(file);
  });
}

function favoriteWork(work) {
  openWork(work)
}

function shuffleRecommendations() {
  if (works.value.length <= 1) return;
  recommendationOffset.value = (recommendationOffset.value + Math.min(5, works.value.length - 1)) % works.value.length;
  chooseShowcaseWork();
}
</script>

<style scoped>
.editorial-home {
  display: grid;
  gap: 22px;
  padding: 30px 0 54px
}

.editorial-lead {
  display: grid;
  grid-template-columns:minmax(0, 2fr) minmax(310px, .9fr);
  gap: 26px;
  min-height: 440px
}

.lead-story {
  position: relative;
  min-height: 440px;
  overflow: hidden;
  border-radius: 17px;
  background-position: center;
  background-size: cover;
  cursor: pointer
}

.lead-shade {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(12, 12, 16, .78) 0, rgba(12, 12, 16, .38) 52%, rgba(12, 12, 16, .06) 100%)
}

.lead-content {
  position: relative;
  z-index: 1;
  width: min(480px, 62%);
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 34px;
  color: #fff
}

.today-badge {
  padding: 8px 12px;
  border: 1px solid rgba(255, 255, 255, .34);
  border-radius: 7px;
  background: rgba(20, 18, 28, .28);
  font-size: 12px;
  font-weight: 900;
  backdrop-filter: blur(8px)
}

.lead-content h1 {
  max-width: 470px;
  margin: 15px 0 14px;
  white-space: pre-line;
  font-size: clamp(36px, 4vw, 52px);
  line-height: 1.04;
  letter-spacing: -.045em
}

.lead-author {
  display: flex;
  align-items: center;
  gap: 8px
}

.lead-author span {
  width: 35px;
  height: 35px;
  display: grid;
  place-items: center;
  border: 2px solid rgba(255, 255, 255, .7);
  border-radius: 50%;
  background: rgba(255, 255, 255, .18);
  font-weight: 900
}

.lead-author strong {
  font-size: 12px
}

.lead-content p {
  display: -webkit-box;
  max-width: 430px;
  margin: 13px 0 19px;
  overflow: hidden;
  color: rgba(255, 255, 255, .84);
  font-size: 12px;
  line-height: 1.75;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3
}

.lead-content > a {
  min-height: 43px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 18px;
  border-radius: 12px;
  color: #fff;
  background: var(--ui-primary);
  font-size: 12px;
  font-weight: 900;
  text-decoration: none
}

.lead-dots {
  position: absolute;
  z-index: 2;
  left: 50%;
  bottom: 18px;
  display: flex;
  gap: 7px;
  transform: translateX(-50%)
}

.lead-dots i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgba(255, 255, 255, .8)
}

.lead-dots i.active {
  width: 16px;
  border-radius: 99px;
  background: var(--ui-primary)
}

.trending-panel {
  display: grid;
  grid-template-rows:auto repeat(3, 1fr);
  gap: 8px
}

.trending-panel header {
  display: flex;
  align-items: center;
  justify-content: space-between
}

.trending-panel h2 {
  margin: 0;
  font-size: 18px
}

.trending-panel header button {
  border: 0;
  background: transparent;
  color: var(--ui-text-secondary)
}

.trending-panel > button {
  min-width: 0;
  display: grid;
  grid-template-columns:26px 104px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer
}

.trending-panel img {
  width: 104px;
  height: 96px;
  border-radius: 10px;
  object-fit: cover
}

.rank {
  align-self: start;
  margin-top: 5px;
  padding: 6px 0;
  border-radius: 7px 0 0 7px;
  color: #fff;
  background: var(--ui-coral);
  font-weight: 900;
  text-align: center
}

.trending-panel > button:nth-child(3) .rank {
  background: #f5a342
}

.trending-panel > button:nth-child(4) .rank {
  background: #f5c04d
}

.trend-copy {
  min-width: 0;
  display: grid;
  gap: 5px
}

.trend-copy strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px
}

.trend-copy small {
  justify-self: start;
  padding: 4px 7px;
  border-radius: 999px;
  color: var(--ui-primary);
  background: var(--ui-primary-soft);
  font-size: 9px
}

.trend-copy em {
  display: flex;
  align-items: center;
  gap: 3px;
  color: var(--ui-coral);
  font-size: 10px;
  font-style: normal
}

.trending-panel > button > svg {
  color: var(--ui-coral)
}

.editorial-categories {
  display: grid;
  grid-template-columns:repeat(5, minmax(0, 1fr)) minmax(150px, .9fr);
  gap: 14px
}

.editorial-categories a {
  min-height: 47px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid var(--ui-line);
  border-radius: 999px;
  color: var(--ui-text-secondary);
  background: #fff;
  font-size: 12px;
  font-weight: 800;
  text-decoration: none;
  cursor: pointer
}

.editorial-categories a.active {
  border-color: var(--ui-primary);
  color: var(--ui-primary);
  background: var(--ui-primary-soft)
}

.editorial-categories .more-topics {
  background: var(--ui-surface-warm)
}

.recommendations {
  display: grid;
  gap: 12px;
  padding: 15px 17px 20px;
  border: 1px solid var(--ui-line);
  border-radius: 16px;
  background: rgba(255, 255, 255, .55)
}

.recommendations > header {
  display: flex;
  align-items: center;
  justify-content: space-between
}

.recommendations h2 {
  margin: 0;
  font-size: 17px
}

.recommendations header button {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: transparent;
  color: var(--ui-text-secondary);
  font-size: 11px;
  cursor: pointer
}

.editorial-grid {
  display: grid;
  grid-template-columns:repeat(5, minmax(0, 1fr));
  align-items: start;
  gap: 15px
}

.editorial-grid :deep(.work-card) {
  border-radius: 11px;
  box-shadow: none
}

.editorial-grid :deep(.cover) {
  aspect-ratio: 1.15/1
}

.editorial-grid :deep(.copy) {
  padding: 10px
}

.empty-recommend {
  display: grid;
  justify-items: center;
  padding: 45px
}

.empty-recommend button {
  padding: 8px 12px;
  border: 0;
  border-radius: 999px;
  color: #fff;
  background: var(--ui-primary)
}

.home-quickstart {
  display: grid;
  grid-template-columns:minmax(0, 1.65fr) minmax(270px, .75fr);
  gap: 16px
}

.create-banner {
  min-height: 210px;
  display: grid;
  grid-template-columns:minmax(0, 1fr) auto;
  align-items: center;
  gap: 28px;
  padding: 28px 32px;
  border: 1px solid #ded9ff;
  border-radius: 20px;
  background: linear-gradient(130deg, #f8f6ff, #fff 56%, #fff5ed)
}

.section-kicker {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--ui-primary);
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .06em
}

.create-banner h2 {
  max-width: 620px;
  margin: 10px 0 8px;
  font-size: 25px;
  line-height: 1.25;
  letter-spacing: -.025em
}

.create-banner p {
  max-width: 600px;
  margin: 0 0 17px;
  color: var(--ui-text-secondary);
  font-size: 12px;
  line-height: 1.7
}

.create-banner a {
  width: max-content;
  min-height: 42px;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 0 16px;
  border-radius: 999px;
  color: #fff;
  background: var(--ui-primary);
  font-size: 12px;
  font-weight: 900;
  text-decoration: none
}

.create-banner ol {
  display: grid;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none
}

.create-banner li {
  display: flex;
  align-items: center;
  gap: 9px;
  color: var(--ui-text-secondary);
  font-size: 11px
}

.create-banner li span {
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: var(--ui-primary);
  background: #fff;
  box-shadow: var(--ui-shadow-xs);
  font-weight: 900
}

.growth-entry {
  display: grid;
  gap: 12px
}

.growth-entry a {
  min-height: 99px;
  display: grid;
  grid-template-columns:auto 1fr auto;
  align-items: center;
  gap: 13px;
  padding: 18px;
  border: 1px solid var(--ui-line);
  border-radius: 18px;
  color: var(--ui-text);
  background: #fff;
  text-decoration: none;
  box-shadow: var(--ui-shadow-xs)
}

.growth-entry a:first-child > svg {
  color: var(--ui-coral)
}

.growth-entry a:last-child > svg {
  color: #e0aa27
}

.growth-entry span {
  display: grid;
  gap: 4px
}

.growth-entry small {
  color: var(--ui-text-tertiary);
  font-size: 10px
}

.growth-entry strong {
  font-size: 13px
}

.growth-entry a > svg:last-child {
  color: var(--ui-text-tertiary)
}

.inspiration-section {
  display: grid;
  gap: 14px;
  padding: 21px;
  border: 1px solid var(--ui-line);
  border-radius: 20px;
  background: #fff
}

.inspiration-section > header {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 18px
}

.inspiration-section h2 {
  margin: 7px 0 0;
  font-size: 21px
}

.inspiration-section header a {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--ui-primary);
  font-size: 11px;
  font-weight: 800;
  text-decoration: none
}

.inspiration-grid {
  display: grid;
  grid-template-columns:repeat(3, 1fr);
  gap: 13px
}

.inspiration-grid button {
  min-width: 0;
  display: grid;
  grid-template-columns:124px minmax(0, 1fr);
  align-items: center;
  gap: 13px;
  padding: 8px;
  border: 0;
  border-radius: 15px;
  background: var(--ui-bg);
  text-align: left;
  cursor: pointer
}

.inspiration-grid img {
  width: 124px;
  height: 98px;
  object-fit: cover;
  border-radius: 11px
}

.inspiration-grid span {
  min-width: 0;
  display: grid;
  gap: 5px
}

.inspiration-grid small {
  width: max-content;
  padding: 4px 7px;
  border-radius: 999px;
  color: var(--ui-primary);
  background: var(--ui-primary-soft);
  font-size: 9px
}

.inspiration-grid strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px
}

.inspiration-grid em {
  color: var(--ui-text-tertiary);
  font-size: 10px;
  font-style: normal
}

.home-skeleton {
  display: grid;
  grid-template-columns:2fr .9fr;
  gap: 26px;
  min-height: 440px
}

.home-skeleton div, .home-skeleton span {
  border-radius: 17px;
  background: linear-gradient(100deg, #ece9ef, #f8f7f8, #ece9ef);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite
}

.home-skeleton aside {
  display: grid;
  gap: 10px
}

@keyframes shimmer {
  to {
    background-position: -200% 0
  }
}

@media (max-width: 980px) {
  .editorial-lead {
    grid-template-columns:1fr
  }

  .trending-panel {
    grid-template-columns:repeat(3, 1fr);
    grid-template-rows:auto 1fr
  }

  .trending-panel header {
    grid-column: 1/-1
  }

  .trending-panel > button {
    grid-template-columns:24px 80px minmax(0, 1fr)
  }

  .trending-panel img {
    width: 80px;
    height: 75px
  }

  .trending-panel > button > svg {
    display: none
  }

  .editorial-categories {
    grid-template-columns:repeat(3, 1fr)
  }

  .editorial-grid {
    grid-template-columns:repeat(3, 1fr)
  }

  .home-quickstart {
    grid-template-columns:1fr
  }

  .growth-entry {
    grid-template-columns:1fr 1fr
  }

  .inspiration-grid {
    grid-template-columns:1fr
  }

  .inspiration-grid button {
    grid-template-columns:150px 1fr
  }
}

@media (max-width: 680px) {
  .editorial-home {
    gap: 14px;
    padding: 8px 0 30px
  }

  .editorial-lead {
    gap: 0;
    min-height: 0
  }

  .lead-story {
    min-height: 220px
  }

  .lead-content {
    width: 100%;
    justify-content: flex-end;
    padding: 16px
  }

  .today-badge {
    padding: 5px 8px;
    font-size: 9px
  }

  .lead-content h1 {
    max-width: 260px;
    margin: 8px 0 7px;
    font-size: 28px
  }

  .lead-author span {
    width: 27px;
    height: 27px
  }

  .lead-author strong {
    font-size: 9px
  }

  .lead-content p {
    max-width: 285px;
    margin: 7px 0 10px;
    font-size: 9px;
    line-height: 1.45;
    -webkit-line-clamp: 2
  }

  .lead-content > a {
    min-height: 36px;
    padding: 0 14px;
    font-size: 10px
  }

  .lead-dots {
    bottom: 10px
  }

  .trending-panel {
    display: none
  }

  .editorial-categories {
    display: flex;
    overflow-x: auto;
    margin-right: -14px
  }

  .editorial-categories button, .editorial-categories a {
    min-width: 90px;
    min-height: 36px;
    font-size: 10px
  }

  .home-quickstart {
    grid-template-columns:1fr
  }

  .create-banner {
    min-height: 0;
    grid-template-columns:1fr;
    padding: 21px 18px
  }

  .create-banner h2 {
    font-size: 21px
  }

  .create-banner ol {
    display: none
  }

  .create-banner li {
    display: grid;
    justify-items: center;
    text-align: center
  }

  .growth-entry {
    grid-template-columns:repeat(2, minmax(0, 1fr));
    gap: 9px
  }

  .growth-entry a {
    min-height: 72px;
    gap: 8px;
    padding: 13px
  }

  .growth-entry a > svg:last-child {
    display: none
  }

  .growth-entry strong {
    font-size: 11px;
    line-height: 1.35
  }

  .recommendations {
    margin: 0 -14px;
    padding: 12px 14px;
    border-radius: 0
  }

  .editorial-grid {
    grid-template-columns:repeat(2, minmax(0, 1fr));
    gap: 9px
  }

  .inspiration-section {
    margin: 0 -14px;
    padding: 17px 14px;
    border-radius: 0
  }

  .inspiration-section > header {
    align-items: start
  }

  .inspiration-section h2 {
    font-size: 18px
  }

  .inspiration-section header a {
    max-width: 90px;
    text-align: right
  }

  .inspiration-grid button {
    grid-template-columns:108px 1fr
  }

  .inspiration-grid img {
    width: 108px;
    height: 84px
  }

  .home-skeleton {
    grid-template-columns:1fr;
    min-height: 220px
  }

  .home-skeleton aside {
    display: none
  }
}

.lead-content > button {
  min-height: 43px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 18px;
  border: 0;
  border-radius: 12px;
  color: #28233f;
  background: #fff;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer
}

.lead-content > button:hover, .lead-content > button:focus-visible {
  color: var(--ui-primary);
  box-shadow: 0 8px 24px rgba(0, 0, 0, .18)
}

.lead-story {
  cursor: default;
  outline: none
}

.lead-story:focus-visible {
  box-shadow: 0 0 0 4px var(--ui-primary-soft), 0 0 0 2px var(--ui-primary)
}

.lead-content {
  animation: hero-content-in .3s ease both
}

.lead-arrow {
  position: absolute;
  z-index: 3;
  top: 50%;
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  transform: translateY(-50%);
  border: 1px solid rgba(255, 255, 255, .45);
  border-radius: 50%;
  color: #fff;
  background: rgba(20, 18, 28, .3);
  cursor: pointer;
  backdrop-filter: blur(8px)
}

.lead-arrow:hover, .lead-arrow:focus-visible {
  background: rgba(102, 92, 246, .9)
}

.lead-arrow.previous {
  left: auto;
  right: 68px
}

.lead-arrow.next {
  right: 16px
}

.lead-dots {
  z-index: 3
}

.lead-dots button {
  width: 8px;
  height: 8px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, .8);
  cursor: pointer;
  transition: width .2s ease, background-color .2s ease
}

.lead-dots button.active {
  width: 22px;
  border-radius: 99px;
  background: var(--ui-primary)
}

@keyframes hero-content-in {
  from {
    opacity: .45;
    transform: translateY(5px)
  }
  to {
    opacity: 1;
    transform: none
  }
}

@media (max-width: 680px) {
  .lead-arrow {
    display: none
  }
}

@media (prefers-reduced-motion: reduce) {
  .lead-content, .lead-dots button {
    animation: none;
    transition: none
  }
}

.conversion-hero{
  display:grid;
  grid-template-columns:minmax(0,.92fr) minmax(420px,1.08fr);
  gap:clamp(24px,3.4vw,52px);
  align-items:center;
  min-height:548px;
  padding:clamp(34px,4.5vw,64px);
  border:1px solid var(--ui-line);
  border-radius:28px;
  background:radial-gradient(circle at 12% 10%,rgba(255,212,220,.55),transparent 34%),linear-gradient(135deg,#fff 0%,#fbf9ff 52%,#f0efff 100%);
  box-shadow:var(--ui-shadow-sm);
  overflow:hidden;
}
.conversion-copy{display:grid;justify-items:start;min-width:0}
.beta-pill{display:inline-flex;align-items:center;padding:7px 12px;border-radius:999px;color:var(--ui-primary);background:rgba(255,255,255,.8);border:1px solid rgba(101,88,246,.18);font-size:12px;font-weight:900;box-shadow:0 5px 18px rgba(101,88,246,.08)}
.conversion-copy h1{margin:18px 0 0;color:var(--ui-text);font-size:clamp(42px,4vw,56px);line-height:1.04;letter-spacing:-.05em}
.conversion-copy>p{max-width:570px;margin:20px 0 0;color:var(--ui-text-secondary);font-size:16px;line-height:1.7}
.scene-picker{width:100%;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;margin-top:27px}
.scene-picker button{min-width:0;display:flex;align-items:center;gap:10px;padding:13px 14px;border:1px solid var(--ui-line);border-radius:16px;color:var(--ui-text-secondary);background:rgba(255,255,255,.82);text-align:left;cursor:pointer;transition:.18s ease}
.scene-picker button:hover{transform:translateY(-2px);border-color:rgba(101,88,246,.35)}
.scene-picker button.active{color:var(--ui-primary);border-color:var(--ui-primary);background:var(--ui-primary-soft);box-shadow:0 10px 24px rgba(101,88,246,.1)}
.scene-picker span{display:grid;gap:2px;min-width:0}.scene-picker strong{color:var(--ui-text);font-size:13px}.scene-picker small{white-space:nowrap;color:var(--ui-text-tertiary);font-size:10px}
.home-file-input{position:fixed;width:1px;height:1px;opacity:0;pointer-events:none}
.conversion-actions{display:flex;align-items:center;gap:18px;margin-top:22px}
.upload-primary{min-height:54px;display:flex;align-items:center;gap:9px;padding:0 23px;border:0;border-radius:16px;color:#fff;background:linear-gradient(135deg,var(--ui-primary),#826ff8);box-shadow:0 14px 28px rgba(101,88,246,.25);font-size:15px;font-weight:900;cursor:pointer}
.upload-primary:hover{transform:translateY(-1px)}.upload-primary:disabled{opacity:.65;cursor:wait}
.upload-note{margin-top:12px;color:var(--ui-text-tertiary);font-size:11px}
.compact-flow{width:100%;display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:6px;margin-top:25px;padding-top:18px;border-top:1px solid rgba(101,88,246,.1)}
.compact-flow span{position:relative;display:flex;align-items:center;gap:7px;color:var(--ui-text-tertiary);font-size:10px;font-weight:800;white-space:nowrap}
.compact-flow span:not(:last-child)::after{content:"";position:absolute;right:4px;width:14px;height:1px;background:rgba(101,88,246,.18)}
.compact-flow i{width:22px;height:22px;display:grid;place-items:center;border-radius:50%;color:var(--ui-primary);background:#fff;border:1px solid rgba(101,88,246,.14);font-size:9px;font-style:normal;font-weight:950}
.result-showcase{min-width:0;padding:14px;border:1px solid rgba(255,255,255,.74);border-radius:24px;background:rgba(255,255,255,.46);box-shadow:0 24px 60px rgba(67,55,137,.14);backdrop-filter:blur(14px)}
.result-showcase header{display:flex;align-items:center;justify-content:space-between;padding:2px 4px 12px;color:var(--ui-text-tertiary);font-size:11px}.result-showcase header strong{color:var(--ui-primary);font-size:12px}
.showcase-image{position:relative;width:100%;height:390px;display:grid;place-items:center;overflow:hidden;padding:0;border:1px solid rgba(96,80,60,.06);border-radius:18px;background:rgba(250,247,241,.72);cursor:pointer}
.showcase-image img{position:absolute;inset:10px;display:block;width:calc(100% - 20px)!important;height:calc(100% - 20px)!important;object-fit:contain;box-sizing:border-box;filter:drop-shadow(0 12px 18px rgba(46,38,30,.08));transition:transform .28s ease}.showcase-image:hover img{transform:scale(1.012)}
.showcase-image.webgl{background:#111;cursor:grab}.showcase-image.webgl:active{cursor:grabbing}.showcase-image.webgl :deep(.bead-webgl){min-height:100%}
.showcase-image>span{display:grid;justify-items:center;gap:12px;color:var(--ui-text-tertiary);font-weight:850}
.showcase-image em{position:absolute;right:13px;bottom:13px;padding:7px 10px;border-radius:999px;color:#fff;background:rgba(22,20,32,.72);font-size:10px;font-style:normal;font-weight:850;backdrop-filter:blur(8px)}
.stage-tabs{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:7px;margin-top:10px}
.stage-tabs button{min-width:0;display:flex;align-items:center;justify-content:center;gap:5px;padding:10px 5px;border:1px solid transparent;border-radius:12px;color:var(--ui-text-secondary);background:transparent;font-size:11px;font-weight:850;cursor:pointer;opacity:.38;transition:opacity .18s ease,color .18s ease,background-color .18s ease}
.stage-tabs button:hover{opacity:.72}.stage-tabs button.active{color:var(--ui-primary);border-color:rgba(101,88,246,.2);background:rgba(101,88,246,.1);opacity:1}
.showcase-slide-enter-active,.showcase-slide-leave-active{transition:opacity .28s ease,transform .32s ease}
.showcase-slide-enter-from{opacity:0;transform:translateX(22px) scale(.985)}
.showcase-slide-leave-to{opacity:0;transform:translateX(-18px) scale(.985)}
@media(max-width:980px){
  .conversion-hero{grid-template-columns:1fr;min-height:0;padding:36px}.result-showcase{max-width:720px;width:100%;margin:auto}.showcase-image{height:420px}
}
@media(max-width:680px){
  .conversion-hero{gap:24px;margin:0;padding:24px 18px;border-radius:22px}
  .conversion-copy h1{margin-top:14px;font-size:34px;line-height:1.06}
  .conversion-copy>p{margin-top:13px;font-size:13px;line-height:1.65}
  .scene-picker{gap:7px;margin-top:18px}.scene-picker button{justify-content:center;padding:11px 7px}.scene-picker button svg{width:18px}.scene-picker small{display:none}
  .conversion-actions{width:100%;display:grid;grid-template-columns:1fr;gap:13px;margin-top:17px}.upload-primary{justify-content:center;min-height:52px;padding:0 16px;font-size:14px}
  .upload-note{font-size:10px}
  .compact-flow{margin-top:18px;padding-top:14px;gap:3px}.compact-flow span{display:grid;justify-items:center;gap:4px;font-size:9px}.compact-flow span::after{display:none}
  .result-showcase{padding:11px;border-radius:19px}.showcase-image{height:270px;border-radius:14px}.stage-tabs{gap:3px}.stage-tabs button{display:grid;justify-items:center;gap:4px;padding:8px 2px;font-size:9px}
}
</style>
